import React, { useEffect, useState, useRef, useContext, useMemo } from 'react';
import { FieldData, FieldOption, SearchSelectFieldType } from 'types/FieldTypes';
import { useTranslation, Trans } from 'react-i18next';
import { useModal } from 'components/modals/ModalContext';
import { useClickOutside, useSetDropdownWidth, calculateDropdownPosition } from 'services/utils/dropdownBehavior'
import { useFetchSearchHook } from 'services/api/fetchSearchHook'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { saveData } from 'services/api/saveData';
import FormFieldContext from '../FormFieldContext';
import axios from 'axios';
import '../../../style/scss/forms.scss';

interface SearchSelectProps extends SearchSelectFieldType {
    updatedData?: any,
    onPreventClose?: (shouldPreventClose: boolean) => void;
}

const SearchSelect: React.FC<SearchSelectProps & { data?: FieldData }> = ({
    query, onSelect, updatedData, value, placeholder, objectName, name, data, selectionFormat, optionFormat,
    isInvalid, onlyAddUniqueItems = false, disableAddNew = false, onUnlink, addNewItem, disabled,
    onPreventClose, selectionSuffix,
    showOptionsOnClick,
    directOptionsTitle,
    refetchData
}) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { setUpdatedData } = useContext(FormFieldContext);
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [directSearch, setDirectSearch] = useState(false);

    // Resolve the query from the given function or param
    const resolvedQuery = useMemo(() => {
        if (typeof query === 'function') return query(updatedData);
        return query;
    }, [query, updatedData]);

    // Set the query states
    const [activeEndpoint, setActiveEndpoint] = useState(resolvedQuery.endpoint);
    const [activeParams, setActiveParams] = useState(resolvedQuery.params);

    // Get the direct search config, if set
    const directSearchConfig = useMemo(() => {
        if (search !== '') return null;
        if (!showOptionsOnClick || !updatedData) return null;
        return showOptionsOnClick(updatedData);
    }, [search, showOptionsOnClick, updatedData]);
    
    // Determine if the direct search should be executed when clicking on the field
    const isDirectSearchMode = directSearch && (search === '' && !!directSearchConfig);
   
    // Fetch the options
    const { options, loading, fetchFinished, resetLastFetchValue } = useFetchSearchHook(activeEndpoint, activeParams, search, isDirectSearchMode);

    // Other states
    const [selectedOption, setSelectedOption] = useState<FieldOption | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dropdownListRef = useRef<HTMLDivElement>(null);
    const fieldRef = useRef<HTMLTextAreaElement>(null);
    const [dropdownWidth, setDropdownWidth] = useState<number>(200);
    const [dropdownAbove, setDropdownAbove] = useState(false);
    const [dropdownMaxHeight, setDropdownMaxHeight] = useState<number | undefined>(undefined);

    const isExactMatch = () => {
        return onlyAddUniqueItems ? options.some((option) => option.name.toLowerCase() === search.toLowerCase()) : false;
    };

    // Set the current value as selected option
    useEffect(() => {
        // When the item is unlinked, do not set the data again
        if (updatedData && name in updatedData) {
            if (updatedData[name] === null) {
                setSelectedOption(null);
                return;
            }
        }

        // Set the current value as selected option
        if (data && data[name] !== undefined) {
            setSelectedOption(data[name])
        } else if (value) {
            setSelectedOption(value)
        }
    }, [data, name, value]);

    useEffect(() => {
        if (search.length >= 2) {
            setActiveEndpoint(resolvedQuery.endpoint);
            setActiveParams(resolvedQuery.params);
        }
    }, [search, resolvedQuery.endpoint, resolvedQuery.params]);

    useEffect(() => {
        if (search.length >= 2 && directSearch) {
            setDirectSearch(false);
        }
    }, [search, directSearch]);

    useEffect(() => {
        if (fetchFinished && options.length > 0) {
            openDropdown();
        }
    }, [fetchFinished, options]);

    // Helper function that gives the value of an object by it's path
    function getValueByPath(obj: any, path: string) {
        const parts = path.split(/[.\[\]]/).filter((part: string) => part.length);
        return parts.reduce((acc: any, part: string) => acc && acc[part], obj);
    }

    // Format the text in the selection list options
    function formatOption(option: any) {
        let title = '';
        let bracket = '';
        let subtitle = '';

        if (optionFormat) {
            // Format the title
            if (optionFormat.title) {
                const { field, format } = optionFormat.title;
                const value = getValueByPath(option, field);
                title = format(value);
            }

            // Format the optional text between the brackets
            if (optionFormat.bracket) {
                const { field, format, optional } = optionFormat.bracket;
                const value = getValueByPath(option, field);
                if (value || !optional) {
                    // An empty text hides the brackets, so a format can decide per option, which it also receives
                    const bracketText = format(value, option);
                    if (bracketText) bracket = `(${bracketText})`;
                }
            }

            // Format the optional subtitle
            if (optionFormat.subtitle) {
                const { field, format, optional } = optionFormat.subtitle;
                const value = getValueByPath(option, field);
                if (value || !optional) {
                    subtitle = format(value);
                }
            }
        // Set the title in case no option format is given
        } else {
            title = option.name || '';
        }

        // Render the format of the option
        return (
            <React.Fragment>
                <div>{title} {bracket}</div>
                {subtitle && <div className='subtitle'>{subtitle}</div>}
            </React.Fragment>
        );
    }

    // Format the text of the selected option which is shown in the field
    function formatSelection(option: any) {
        const value = option[selectionFormat];

        // Return the raw value if there is no value or no suffix configured
        if (!value || !selectionSuffix) return value;

        // Get the suffix to show behind the value
        const suffix = selectionSuffix(option);

        // Add the suffix behind the value if there is one
        return suffix ? `${value} ${suffix}` : value;
    }

    const handleLinkNewAddedItem = (response: Record<string, any> | Record<string, any>[]) => {
        // On refetch, stop prevent closing
        if (onPreventClose) onPreventClose(false);

        // A bulk form returns a list of items, a single field selects the first one
        const createdItem = Array.isArray(response) ? response[0] : response;
        if (!createdItem) return;

        // Convert the newly added item to an option and handle as selected
        const newAddedItem = { id: createdItem['id'], value: createdItem['id'], name: createdItem[selectionFormat], ...createdItem };

        // Select the new item in the field
        handleSelect(newAddedItem, 'new');
    }

    // Create a new option
    const createNewOption = async (newOption: string) => {
        if (addNewItem) {
            // Call the parent component to add a new item
            if (addNewItem.method === 'callback') {
                addNewItem.onAddNew();
            }
            
            // Open form modal to add a new item
            if (addNewItem.method === 'modal-form') {

                // Prevent closing of the field when editing the modal
                if (onPreventClose) {
                    onPreventClose(true);
                }

                // Add the new option directly to the modal element if it is a function
                const modalElement = typeof addNewItem.modalForm === 'function' ? addNewItem.modalForm(newOption) : addNewItem.modalForm;
        
                // Open the modal
                initializeModal(
                    React.cloneElement(modalElement, { 
                        linkedItem: addNewItem.linkedItem, 
                        searchValue: newOption 
                    }), { 
                        modalSize: 'add-form-smaller', 
                        title: t('button.add_object_label', { object_name: t(objectName) }),
                        onSuccess: (response) => {
                            if (!response) return;

                            handleLinkNewAddedItem(response);

                            // A bulk form returns a list, the callback gets the first created item
                            const createdItem = Array.isArray(response) ? response[0] : response;
                            if (addNewItem?.onSuccess && createdItem) {
                                addNewItem.onSuccess(createdItem, { setUpdatedData });
                            }
                        }
                    }
                );
            }

            // Post directly to add the new item
            if (addNewItem.method === 'direct-post') {
                const source = axios.CancelToken.source();
                try {
                    const data = { name: newOption };
                    await saveData({ apiUrl: addNewItem.postEndpoint, method: 'post', data, source });

                    // Set the new saved option as the selected option
                    setSelectedOption({ value: newOption, name: newOption });
                } catch (error) {
                    console.error('Creating a new option failed', error);
                }
            }

            // Close the dropdown
            setIsOpen(false);
        }         
    }

    // Handle input change
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
    
        // Set search if no option is yet selected
        if (!selectedOption) {
            setSearch(newValue);
            openDropdown();
        }
    };

    // Adjust the field height if the current value exceeds the field height
    useEffect(() => {
        adjustHeight(fieldRef);
    }, [search, selectedOption, selectionFormat]);

    // Handle the selection of an option
    const handleSelect = (option: FieldOption, newItem?: string) => {
        setSelectedOption(option);
        if (onSelect) {
            onSelect(option, newItem);
        }
    };

    // Handle unlink of an option
    const handleUnlink = (e: React.MouseEvent) => {
        e.preventDefault();
        setSelectedOption(null);
        setSearch('');
        if (onUnlink) {
            onUnlink();
        }
        fieldRef.current?.focus();
        resetLastFetchValue();
    }

    // Close the dropdown list when the searchfield is made empty
    useEffect(() => {
        if (search === '') {
            setIsOpen(false);
        }
    }, [search]);

    // Use dropdownBehavior.ts to close the dropdown when clicking outside
    useClickOutside(isOpen, dropdownRef, setIsOpen);

    // Use dropdownBehavior.ts to set the dropdown width on open
    useSetDropdownWidth(fieldRef, setDropdownWidth);

    // Calculate the height of the text to optionally resize the text area height
    const adjustHeight = (fieldRef: React.RefObject<HTMLTextAreaElement>) => {
        if (fieldRef.current) {
            fieldRef.current.style.height = 'auto';
            const newHeight = fieldRef.current.scrollHeight;

            if (newHeight > 34) {
                fieldRef.current.style.height = `${newHeight}px`;
            } else {
                fieldRef.current.style.height = '34px';
            }
        }
    }

    // Handle the opening of the dropdown
    const openDropdown = () => {
        if (!fieldRef.current) return;

        // Calculate the dropdown position
        calculateDropdownPosition(fieldRef, setDropdownAbove, setDropdownMaxHeight);

        // Open the dropdown
        setIsOpen(true);
    };

    return (
        <div className='searchselect-container'>
            <textarea 
                name={name}
                ref={fieldRef}
                value={selectedOption ? formatSelection(selectedOption) : (search || '')}
                onChange={handleInputChange}
                onClick={() => { 
                    if (selectedOption && !selectedOption[selectionFormat]) {
                        setSelectedOption(null);
                    }

                    // Execute direct search on clicking the field
                    if (search === '' && directSearchConfig) {
                        setActiveEndpoint(directSearchConfig.endpoint);
                        setActiveParams(directSearchConfig.params);
                        setDirectSearch(true);
                    }
                }}
                className={`${isInvalid ? 'is-invalid' : ''} ${disabled ? 'disabled' : ''}`}
                placeholder={t(placeholder)}
                readOnly={!!selectedOption}
                rows={1}
                autoComplete="new-password"
                data-lpignore="true"
                data-1p-ignore
            />
            {loading && <div className="loader"></div>}
            {selectedOption && selectedOption[selectionFormat] && !disabled && (
                <div className='clear-button tooltip-icon' 
                     onClick={(e) => handleUnlink(e)}>
                    <FontAwesomeIcon icon={faTimes} />
                    <span className="tooltip">{t('forms.unlink_label')} {t(objectName)}</span>
                </div>
            )}
            {isOpen && !disabled && !selectedOption && !loading && fetchFinished && (
                <div className={`custom-dropdown ${dropdownAbove ? 'dropdown-above' : ''}`} ref={dropdownRef} style={{width: (dropdownWidth - 2), maxHeight: dropdownMaxHeight}}>
                    {options.length > 0 ? (
                        <div className='custom-dropdown-list' style={{width: dropdownWidth}}>
                            <div className='searchselect-options' ref={dropdownListRef}>

                                {isDirectSearchMode && directOptionsTitle && (
                                    <div className="custom-dropdown-title">
                                        {t(directOptionsTitle)}
                                    </div>
                                )}

                                {options.map((option: FieldOption) => {
                                    let selectedOptionId = null;
                                    if (selectedOption !== null) {
                                        selectedOptionId = (selectedOption as FieldOption).id;
                                    }
                                    return (
                                        <div key={option.id || option.name} 
                                            className={`custom-dropdown-item ${selectedOption && selectedOptionId === option.id ? 'active' : ''}`}
                                            onClick={() => handleSelect(option)}>
                                                {formatOption(option)}
                                        </div>
                                    )
                                })}
                                {options.length >= 50 && 
                                    <div className="limit-message">
                                        {t('forms.dropdown_option_limit_message')}
                                    </div>
                                }
                            </div>
                        </div>
                    ) : (
                        <div className='custom-dropdown-list' style={{width: dropdownWidth}}>
                            <div className='searchselect-options' ref={dropdownListRef}>
                                <div className='custom-dropdown-item-no-options'>
                                    {t('forms.no_results_found')}
                                </div>
                            </div>
                        </div>
                    )}
                    {!isExactMatch() && fetchFinished && !disableAddNew && !isDirectSearchMode && (
                        <div className='searchselect-add' onClick={() => createNewOption(search)}>
                            <div className='add-label'>
                                <Trans
                                    i18nKey="forms.searchselect_add_new_label"
                                    values={{ search: search, objectName: t(objectName) }}
                                />
                                </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchSelect;